// ── Pack / piece helpers ──────────────────────────────────────────────────────
// The whole app agrees on ONE representation, so stock can never drift apart:
//   product.purchase_price / sale_price ... price of ONE piece
//   product.units_per_bulk ................ pieces inside one pack/box
//   product.stock_quantity ................. total pieces on the shelf
//   invoice item quantity .................. PIECES (this is what stock moves by)
//   invoice item unit_price ................ price of ONE piece
// The Packs/Pieces selector only changes what the entry boxes DISPLAY: the
// inputs convert to pieces as the cashier types, so quantity and unit_price
// are pieces/per-piece before anyone totals them. normalizeLineItem() is the
// one place that re-derives the display-only pack columns for printing, so a
// pack/piece mix-up can never change what stock is moved or what is charged.
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const numOr = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

// Pieces in one pack for this line. The PRODUCT is the source of truth for
// its own pack size: a row only keeps a copy of it from the moment the product
// was picked, and asking that copy first is how one pack could come off the
// shelf at another product's size — picking a 12-pack product and then this
// 24-pack one still left the row saying 12, so "1 pack" moved 12 pieces. The
// row's own value stands in only when there is nothing to ask: a custom item,
// or a line whose product has been removed.
// (units_per_bulk of 1 means the product has no pack concept at all.)
const packSizeFor = (item, product) => {
  const own = (v) => (v !== undefined && v !== null && v !== '' ? v : null);
  const raw = own(product && product.units_per_bulk) ?? own(item && item.units_per_bulk) ?? 1;
  const n = numOr(raw, 1);
  return n > 1 ? n : 1;
};

// Exact piece/pack breakdown for a stock figure — "6 packs + 8 pcs", "5 packs",
// "8 pcs". Display only: it never feeds a calculation. Returns '' when the
// product has no pack concept (unitsPerPack <= 1) or the input is unusable, so
// an empty field can never render as "NaN" and a whole pack is never rounded
// into a misleading "≈ N packs". Negative stock (only reachable by writing to
// the API directly) is reported as the piece shortfall it actually is.
const packBreakdown = (stock, unitsPerPack, t) => {
  const upb = Math.round(numOr(unitsPerPack, 0));
  const total = round2(numOr(stock, NaN));
  if (!(upb > 1) || !Number.isFinite(total)) return '';

  const fullPacks = total > 0 ? Math.floor(total / upb) : 0;
  const loosePcs = round2(total - fullPacks * upb);
  const pcs = (n) => `${n} ${Math.abs(n) === 1 ? t('pcSingular') : t('pcPlural')}`;

  if (fullPacks <= 0) return pcs(loosePcs);
  const packText = `${fullPacks} ${fullPacks === 1 ? t('pack') : t('packs')}`;
  return loosePcs > 0 ? `${packText} + ${pcs(loosePcs)}` : packText;
};

// Formats a money figure the API returned. Thousand separators only — the
// value itself is never adjusted here.
const moneyText = (value) => Number(value ?? 0).toLocaleString(undefined, {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
});

// Piece counts are plain numbers — "120 pcs", never "120.00 pcs" — and a
// fraction only survives when it is really there.
const pcsText = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// Prices keep two decimals ("Rs. 126.00") so a pack price sits consistently
// under its per-piece line. Purely presentational — the API already rounded.
const priceText = (value) => Number(value ?? 0).toFixed(2);

// The Add/Edit boxes COUNT in the unit of the Units Quantity field: 1 →
// pieces, more than 1 → packs. The API always stores pieces, so these helpers
// only put the stored figure into the box's unit and take it back on save.
// One predicate decides that unit for every stock screen.
const packMode = (unitsPerPack) => numOr(unitsPerPack, 1) > 1;
const stockQtyType = (unitsPerPack) => (packMode(unitsPerPack) ? 'pack' : 'piece');

// "120.00" reads badly in a whole-piece box: only a run of zeros after the
// point goes (120.00 → 120, 120.50 stays 120.50).
const trimCount = (value) => String(value ?? '').replace(/^(\d+)\.0+$/, '$1');

// The pack count a box shows for a stored piece total — 125 pcs of 12 is
// "10.42". Plain wording wins, but never at the cost of meaning the same
// stock: a pack count that no longer round-trips would quietly move pieces
// the moment the box is saved, so the shortest figure that gets back to the
// same total it came from is the one used.
const packInputValue = (pieces, unitsPerPack) => {
  const total = numOr(pieces, NaN);
  const perPack = numOr(unitsPerPack, 1);
  if (!Number.isFinite(total)) return String(pieces ?? '');
  const exact = total / perPack;
  for (const digits of [2, 4, 6]) {
    const rounded = Math.round(exact * 10 ** digits) / 10 ** digits;
    if (Math.round(rounded * perPack) === Math.round(total)) return String(rounded);
  }
  return String(exact);
};

// Units Quantity is a LABEL on the other boxes, not an instruction to rewrite
// them: it says how their numbers are READ — 1 means pieces, more than 1 means
// packs — and the multiplying and dividing belongs to one place only, on save.
// So the boxes keep exactly what was typed. Rewording them here instead made
// 960 become 11520 the moment Units was set to 12, and turned 15 into 1.25.
const changeUnits = (form, nextUnits) => ({ ...form, units_per_bulk: nextUnits });

// A stored piece total split into the two boxes the editor fills: whole packs
// and whatever is left over. 80 pieces of 12 is "6 packs, 8 loose" — never the
// 6.67 one pack box would have to show, an invitation to re-save stock into a
// different shape. Below one pack the pack box reads 0 and the shortfall sits
// in the loose box, where it cannot be mistaken for a part-pack.
const splitIntoPacks = (pieces, unitsPerPack) => {
  const total = round2(numOr(pieces, 0));
  const perPack = Math.round(numOr(unitsPerPack, 1));
  if (!(perPack > 1)) return { packs: trimCount(total), loose: '' };
  const fullPacks = total > 0 ? Math.floor(total / perPack) : 0;
  return {
    packs: String(fullPacks),
    loose: String(round2(total - fullPacks * perPack)),
  };
};

// The one piece count a packs + loose pair of boxes stands for. Neither half
// means anything on its own, so this is the only figure they agree on.
const totalPieces = (packs, loose, unitsPerPack) =>
  Math.round(numOr(packs, 0)) * Math.round(numOr(unitsPerPack, 1))
  + Math.round(numOr(loose, 0));

// "Total: 80 pieces" — what the two boxes add up to, spelled out under them.
const totalPiecesText = (packs, loose, unitsPerPack, t) => {
  const total = totalPieces(packs, loose, unitsPerPack);
  return t('total') + ': ' + pcsText(total) + ' '
    + (total === 1 ? t('piece') : t('pieces'));
};

// Opening the editor fills the boxes in the unit they will be saved in, so a
// re-save can never multiply or divide what is already stored.
// Prices are stored per piece; display per pack when units_per_bulk > 1.
const productToEditForm = (p) => {
  const upb = numOr(p.units_per_bulk, 1);
  const inPacks = upb > 1;
  const purchasePerPiece = numOr(p.purchase_price, 0);
  const salePerPiece = numOr(p.sale_price, 0);
  // Stock and the low-stock limit are stored in pieces; counted in packs they
  // are handed to the boxes split, and a piece-only product keeps one box.
  const stock = splitIntoPacks(p.stock_quantity, upb);
  const alert = splitIntoPacks(p.min_stock_alert, upb);
  return {
    ...p,
    stock_quantity: inPacks ? stock.packs : trimCount(p.stock_quantity),
    stock_loose: inPacks ? stock.loose : '',
    min_stock_alert: inPacks ? alert.packs : trimCount(p.min_stock_alert),
    alert_loose: inPacks ? alert.loose : '',
    purchase_price: inPacks ? String(round2(purchasePerPiece * upb)) : String(purchasePerPiece),
    sale_price: inPacks ? String(round2(salePerPiece * upb)) : String(salePerPiece),
    units_per_bulk: p.units_per_bulk ? String(p.units_per_bulk) : '1',
  };
};

// How much stock is on the shelf, written the way it is counted: "6 packs +
// 8 pcs", "5 packs", "12 pcs". The words ride with the number so a pack figure
// can never be read as pieces, and a product with no pack concept simply reads
// as pieces. Display only — it never feeds a calculation.
const stockStatusText = (stock, unitsPerPack, t) => {
  const breakdown = packBreakdown(stock, unitsPerPack, t);
  if (breakdown) return breakdown;
  const total = round2(numOr(stock, NaN));
  if (!Number.isFinite(total)) return `0 ${t('pcPlural')}`;
  return `${pcsText(total)} ${total === 1 ? t('pcSingular') : t('pcPlural')}`;
};

// Switching the "Pieces / Packs" selector only changes what the boxes DISPLAY.
// The stored quantity is always pieces and the stored price is always the
// per-piece price, so a pack/piece mix-up can never corrupt stock or money.
const normalizeLineItem = (item, productList) => {
  if (!item) return item;
  if (item._normalized) return item; // already resolved — never twice

  const product = (item.product_id && productList)
    ? (productList.find(p => p.id === item.product_id) || null)
    : null;
  const upb = packSizeFor(item, product);
  const asPacks = item.qty_type === 'pack' && upb > 1;

  const pieces = round2(numOr(item.quantity, 0));   // PIECES — what stock moves by
  const perPiece = numOr(item.unit_price, 0);       // per piece — the product's price

  // Display-only breakdown for printed / WhatsApp invoices: whole packs plus
  // any loose pieces, so "2 packs" never has to be typed as "24".
  let bulk_quantity = null;
  let small_quantity = null;
  let bulk_unit_name = null;
  let bulk_unit_price = null;
  let small_unit_price = null;
  if (asPacks) {
    const packs = Math.floor(pieces / upb);
    bulk_quantity = packs;
    small_quantity = round2(pieces - (packs * upb));
    bulk_unit_name = product ? (product.bulk_unit || 'Pack') : 'Pack';
    bulk_unit_price = round2(perPiece * upb);
    small_unit_price = perPiece;
  }

  return {
    ...item,
    quantity: pieces,
    unit_price: perPiece,
    subtotal: round2(pieces * perPiece),
    units_per_bulk: upb,
    bulk_quantity,
    small_quantity,
    bulk_unit_name,
    bulk_unit_price,
    small_unit_price,
    _normalized: true,
  };
};

// "2 Packs (24 pcs)" for a resolved line — used by previews / WhatsApp.
const describeLineQty = (row) => {
  const upb = numOr(row.units_per_bulk, 1);
  const packs = numOr(row.bulk_quantity, 0);
  if (packs > 0 && upb > 1) {
    const unitLabel = row.bulk_unit_name || 'Pack';
    const loose = numOr(row.small_quantity, 0);
    const head = `${packs} ${unitLabel}${packs === 1 ? '' : 's'}`;
    return loose > 0
      ? `${head} + ${loose} pcs (${numOr(row.quantity, 0)} pcs)`
      : `${head} (${numOr(row.quantity, 0)} pcs)`;
  }
  return String(row.quantity ?? 0);
};

// A fresh line defaults to 1. If that line is in pack mode, "1" should read as
// 1 pack rather than 0.083 of one. Only the untouched default of 1 piece is
// adjusted — any other quantity keeps its exact meaning in pieces, so nothing
// the cashier typed is ever silently reinterpreted.
const initialPiecesFor = (qtyType, upb, quantity) =>
  (qtyType === 'pack' && upb > 1 && numOr(quantity, 0) === 1) ? upb : quantity;

export {
  round2, numOr, packSizeFor, packBreakdown, moneyText, pcsText, priceText,
  packMode, stockQtyType, trimCount, packInputValue,
  changeUnits, splitIntoPacks, totalPieces, totalPiecesText,
  productToEditForm, stockStatusText, normalizeLineItem,
  describeLineQty, initialPiecesFor,
};
